import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';
import { checkIfDateIsToday, getHoursAndMinutes } from 'utils/functions/dateTime';
import Popup from 'modules/shared/common/components/Popup';
import DatePicker from 'modules/shared/common/components/DatePicker';
import TimeSlotsDropdown from 'modules/shared/common/components/TimeSlotsDropdown';
import RadioButtonGroup from 'modules/shared/common/components/RadioButtonGroup';
import Loader from 'modules/shared/common/components/Loader';
import SectionDivider from 'modules/shared/common/components/SectionDivider';
import { useDispatch } from 'react-redux';
import { paymentMethodsKeys, status } from 'constants/general';
import Can from 'modules/shared/common/components/Can';
import SearchBar from 'modules/shared/common/components/SearchBar';
import CheckboxGroup from 'modules/shared/common/components/CheckboxGroup';
import { initialNextOrderStateDefault, initialNextOrderStateLabelDefault } from './helper';
import {
  OrderDetailsHeaderWrapper,
  OrderDetailsHeaderOrderInfoWrapper,
  OrderDetailsHeaderOrderBtnWrapper,
  OrderDetailsHeaderIconBtnWrapper,
  OrderDetailsHeaderIconBtn,
  OrderDetailsHeaderIconBtnMenu,
  OrderDetailsHeaderIconBtnMenuItem,
  OrderDetailsHeaderOrderId,
  OrderDetailsHeaderViewedBtn,
  OrderDetailsHeaderProcessedBtn,
  OrderDetailsHeaderResultsDoneBtn,
  OrderDetailsHeaderScheduleBtn,
  OrderDetailsHeaderActionsPopupWrapper,
  OrderDetailsHeaderActionsPopupTitleWrapper,
  OrderDetailsHeaderActionsPopupTitle,
  OrderDetailsHeaderActionsPopupBtnWrapper,
  OrderDetailsHeaderActionsPopupYesBtn,
  OrderDetailsHeaderActionsPopupNoBtn,
  OrderDetailsScheduleOrderPopupWrapper,
  OrderDetailsScheduleOrderPopupTitleWrapper,
  OrderDetailsScheduleOrderPopupTitle,
  OrderDetailsScheduleOrderPopupTitleSpan,
  OrderDetailsScheduleOrderSectionTitle,
  OrderDetailsScheduleOrderDateTimeSectionWrapper,
  OrderDetailsScheduleOrderDateTimeWrapper,
  OrderDetailsScheduleOrderReasonsWrapper,
  OrderDetailsScheduleOrderNoteWrapper,
  OrderDetailsScheduleOrderNoteTextArea,
  OrderDetailsScheduleOrderBtnWrapper,
  OrderDetailsScheduleOrderSaveBtn,
  OrderDetailsScheduleOrderCancelBtn,
  OrderDetailsScheduleOrderError,
  OrderDetailsCancelSectionSubTitle,
  OrderState,
  SelectedItem,
  Label,
  DeleteIcon,
} from './OrderDetailsHeader.style';

// ###############################################################
// <---------------------- Sub Sections ------------------------->
// ###############################################################
const OrderActionsPopup = ({
  themeContext,
  stringsData,
  isActionPopupLoading,
  actionStatus,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
  orderStatusEnum,
}) => (
  <Popup handleCloseBtnClick={() => handleActionPopupDismissBtnClick()}>
    <OrderDetailsHeaderActionsPopupWrapper>
      <OrderDetailsHeaderActionsPopupTitleWrapper>
        <OrderDetailsHeaderActionsPopupTitle>
          {stringsData.orderDetailsHeader.actionPopupTitle(
            orderStatusEnum.getKeyByValue(actionStatus),
          )}
        </OrderDetailsHeaderActionsPopupTitle>
      </OrderDetailsHeaderActionsPopupTitleWrapper>
      <SectionDivider
        gap={16}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <OrderDetailsHeaderActionsPopupBtnWrapper>
        {(isActionPopupLoading && <Loader width={28} height={28} thickness={4} />) || (
          <>
            <OrderDetailsHeaderActionsPopupYesBtn
              as="button"
              onClick={() => handleActionPopupConfirmBtnClick()}
            >
              {stringsData.orderDetailsHeader.actionPopupYesBtn}
            </OrderDetailsHeaderActionsPopupYesBtn>
            <OrderDetailsHeaderActionsPopupNoBtn
              as="button"
              onClick={() => handleActionPopupDismissBtnClick()}
            >
              {stringsData.orderDetailsHeader.actionPopupNoBtn}
            </OrderDetailsHeaderActionsPopupNoBtn>
          </>
        )}
      </OrderDetailsHeaderActionsPopupBtnWrapper>
    </OrderDetailsHeaderActionsPopupWrapper>
  </Popup>
);
OrderActionsPopup.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  stringsData: PropTypes.shape({
    orderDetailsHeader: PropTypes.shape({
      actionPopupTitle: PropTypes.func,
      actionPopupYesBtn: PropTypes.string,
      actionPopupNoBtn: PropTypes.string,
    }),
  }).isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  actionStatus: PropTypes.number.isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
  orderStatusEnum: PropTypes.shape({
    getKeyByValue: PropTypes.func,
  }).isRequired,
};

const reasonsToEnableSelectItemsSection = {
  56: 'Shortage',
  40: 'Fulfillment Issue',
};

const renderSelectItemsSection = (
  orderData,
  selectedChildReasonId,
  selectedItems,
  setSelectedItems,
  selectItemsSearchApi,
) => {
  const { mappedServicesList, orderAttachments } = orderData;
  return (
    reasonsToEnableSelectItemsSection[selectedChildReasonId] && (
      <>
        <OrderState>Select Items</OrderState>
        {mappedServicesList.length > 0 && (
          <CheckboxGroup
            options={mappedServicesList}
            selectedOptions={selectedItems}
            setSelectedOptions={setSelectedItems}
          />
        )}
        {orderAttachments.length > 0 && (
          <>
            <SearchBar
              selectHandler={item => {
                const itemIndex = selectedItems.findIndex(
                  selectedItem => selectedItem.serviceKey === item.key,
                );
                let tempItem = { ...item };
                if (itemIndex === -1) {
                  tempItem = {
                    searchResultItem: true,
                    serviceName: item?.productNameEn,
                    serviceKey: item?.productKey,
                  };
                  setSelectedItems([...selectedItems, tempItem]);
                }
              }}
              objectMapper={item => ({
                name: item?.productNameEn,
                info: item?.newPrice && `${item?.newPrice} EGP`,
              })}
              getData={selectItemsSearchApi}
            />
            {selectedItems
              .filter(({ searchResultItem }) => searchResultItem)
              .map(selectedItem => (
                <SelectedItem>
                  <Label>{selectedItem?.serviceName}</Label>
                  <DeleteIcon
                    onClick={() => {
                      let tempSelected = [...selectedItems];
                      tempSelected = tempSelected.filter(
                        item => item.serviceKey !== selectedItem?.serviceKey,
                      );
                      setSelectedItems(tempSelected);
                    }}
                  />
                </SelectedItem>
              ))}
          </>
        )}
      </>
    )
  );
};

const ScheduleOrderPopup = ({
  themeContext,
  stringsData,
  isScheduleOrderPopupLoading,
  scheduleOrderSelectedDate,
  scheduleOrderTimeSlotsArr,
  scheduleOrderSelectedTime,
  scheduleOrderReason,
  scheduleOrderNote,
  handleScheduleOrderDatePickerChange,
  handleScheduleOrderTimeSlotDropdownMenuChange,
  handleScheduleOrderReasonRadioButtonClick,
  handleScheduleOrderNoteChange,
  handleScheduleOrderPopupConfirmBtnClick,
  handleScheduleOrderPopupDismissBtnClick,
  scheduleOrderReasons,
  scheduleOrderReasonIsRequiredError,
  orderData,
  selectedItems,
  setSelectedItems,
  selectItemsSearchApi,
}) => {
  let scheduleOrderReasonsArr = [];
  if (scheduleOrderReasons.status === status.SUCCESS) {
    scheduleOrderReasonsArr = scheduleOrderReasons.data.map(reason => ({
      name: reason.name,
      key: reason.id,
    }));
  }

  const objectMapper = {
    key: 'key',
    name: 'name',
  }

  return (
    <Popup showCloseBtn={false}>
      <OrderDetailsScheduleOrderPopupWrapper>
        <OrderDetailsScheduleOrderPopupTitleWrapper>
          <OrderDetailsScheduleOrderPopupTitle as="p">
            {stringsData.orderDetailsHeader.scheduleOrderTitle}
          </OrderDetailsScheduleOrderPopupTitle>
          <OrderDetailsScheduleOrderPopupTitleSpan
            as="span"
            onClick={() => handleScheduleOrderPopupDismissBtnClick()}
          />
        </OrderDetailsScheduleOrderPopupTitleWrapper>
        <SectionDivider
          gap={16}
          withSeparator
          separatorGap={16}
          borderColor={themeContext.colors.grey004}
        />
        <OrderDetailsScheduleOrderDateTimeSectionWrapper>
          <OrderDetailsScheduleOrderDateTimeWrapper>
            <OrderDetailsScheduleOrderSectionTitle as="p">
              {stringsData.orderDetailsHeader.scheduleOrderDateLabel}
            </OrderDetailsScheduleOrderSectionTitle>
            <SectionDivider gap={4} />
            <DatePicker
              selectedDate={scheduleOrderSelectedDate}
              handleDatePickerValueChange={handleScheduleOrderDatePickerChange}
            />
          </OrderDetailsScheduleOrderDateTimeWrapper>
          <OrderDetailsScheduleOrderDateTimeWrapper>
            <OrderDetailsScheduleOrderSectionTitle as="p">
              {stringsData.orderDetailsHeader.scheduleOrderTimeLabel}
            </OrderDetailsScheduleOrderSectionTitle>
            <SectionDivider gap={4} />
            <TimeSlotsDropdown
              timeSlotsArr={scheduleOrderTimeSlotsArr}
              selectedTimeSlot={scheduleOrderSelectedTime}
              handleTimeSlotDropdownMenuChange={handleScheduleOrderTimeSlotDropdownMenuChange}
              objectMapper={objectMapper}
            />
          </OrderDetailsScheduleOrderDateTimeWrapper>
        </OrderDetailsScheduleOrderDateTimeSectionWrapper>
        <SectionDivider
          gap={16}
          withSeparator
          separatorGap={16}
          borderColor={themeContext.colors.grey004}
        />
        <OrderDetailsScheduleOrderReasonsWrapper>
          <OrderDetailsScheduleOrderSectionTitle as="p">
            {stringsData.orderDetailsHeader.scheduleOrderReasonTitle}
          </OrderDetailsScheduleOrderSectionTitle>
          <SectionDivider gap={14} />
          <RadioButtonGroup
            RadioButtonGroupArr={scheduleOrderReasonsArr}
            selectedRadioButton={scheduleOrderReason}
            handleRadioButtonClick={handleScheduleOrderReasonRadioButtonClick}
          />
          {renderSelectItemsSection(
            orderData,
            scheduleOrderReason,
            selectedItems,
            setSelectedItems,
            selectItemsSearchApi,
          )}
          {scheduleOrderReasonIsRequiredError && (
            <OrderDetailsScheduleOrderError>
              {stringsData.orderDetailsHeader.scheduleOrderReasonIsRequired}
            </OrderDetailsScheduleOrderError>
          )}
        </OrderDetailsScheduleOrderReasonsWrapper>
        <SectionDivider
          gap={22}
          withSeparator
          separatorGap={16}
          borderColor={themeContext.colors.grey004}
        />
        <OrderDetailsScheduleOrderNoteWrapper>
          <OrderDetailsScheduleOrderSectionTitle as="p">
            {stringsData.orderDetailsHeader.scheduleOrderNoteTitle}
          </OrderDetailsScheduleOrderSectionTitle>
          <SectionDivider gap={8} />
          <OrderDetailsScheduleOrderNoteTextArea
            placeholder={stringsData.orderDetailsHeader.scheduleOrderNotePlaceholder}
            value={scheduleOrderNote}
            onChange={event => handleScheduleOrderNoteChange(event)}
          />
        </OrderDetailsScheduleOrderNoteWrapper>
        <SectionDivider
          gap={16}
          withSeparator
          separatorGap={16}
          borderColor={themeContext.colors.grey004}
        />
        <OrderDetailsScheduleOrderBtnWrapper>
          <OrderDetailsScheduleOrderCancelBtn
            as="button"
            onClick={() => handleScheduleOrderPopupDismissBtnClick()}
          >
            {stringsData.orderDetailsHeader.scheduleOrderCancelBtn}
          </OrderDetailsScheduleOrderCancelBtn>
          <OrderDetailsScheduleOrderSaveBtn
            as="button"
            onClick={() => handleScheduleOrderPopupConfirmBtnClick()}
            disabled={
              reasonsToEnableSelectItemsSection[scheduleOrderReason] && selectedItems.length === 0
            }
          >
            {(isScheduleOrderPopupLoading && <Loader width={14} height={14} thickness={4} />) ||
              stringsData.orderDetailsHeader.scheduleOrderSaveBtn}
          </OrderDetailsScheduleOrderSaveBtn>
        </OrderDetailsScheduleOrderBtnWrapper>
      </OrderDetailsScheduleOrderPopupWrapper>
    </Popup>
  );
};
ScheduleOrderPopup.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  stringsData: PropTypes.shape({
    orderDetailsHeader: PropTypes.shape({
      scheduleOrderTitle: PropTypes.string,
      scheduleOrderDateLabel: PropTypes.string,
      scheduleOrderTimeLabel: PropTypes.string,
      scheduleOrderReasonTitle: PropTypes.string,
      scheduleOrderReasons: PropTypes.shape({
        patientRequest: PropTypes.string,
        fulfillmentIssue: PropTypes.string,
        patientUnreachable: PropTypes.string,
        businessDevelopmentOrder: PropTypes.string,
      }),
      scheduleOrderInsuranceTitle: PropTypes.string,
      scheduleOrderInsuranceSystemDown: PropTypes.string,
      scheduleOrderNoteTitle: PropTypes.string,
      scheduleOrderNotePlaceholder: PropTypes.string,
      scheduleOrderSaveBtn: PropTypes.string,
      scheduleOrderCancelBtn: PropTypes.string,
      scheduleOrderReasonIsRequired: PropTypes.string,
    }),
  }).isRequired,
  isScheduleOrderPopupLoading: PropTypes.bool.isRequired,
  scheduleOrderSelectedDate: PropTypes.shape({}).isRequired,
  scheduleOrderTimeSlotsArr: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  scheduleOrderSelectedTime: PropTypes.shape({}).isRequired,
  scheduleOrderReason: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  scheduleOrderNote: PropTypes.string.isRequired,
  handleScheduleOrderDatePickerChange: PropTypes.func.isRequired,
  handleScheduleOrderTimeSlotDropdownMenuChange: PropTypes.func.isRequired,
  handleScheduleOrderReasonRadioButtonClick: PropTypes.func.isRequired,
  handleScheduleOrderNoteChange: PropTypes.func.isRequired,
  handleScheduleOrderPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleScheduleOrderPopupDismissBtnClick: PropTypes.func.isRequired,
  scheduleOrderReasons: PropTypes.shape({
    status: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  scheduleOrderReasonIsRequiredError: PropTypes.bool,
  orderData: PropTypes.shape({}),
  selectedItems: PropTypes.shape([]),
  setSelectedItems: PropTypes.func,
  selectItemsSearchApi: PropTypes.func,
};
ScheduleOrderPopup.defaultProps = {
  scheduleOrderReasonIsRequiredError: false,
  orderData: {},
  selectedItems: [],
  setSelectedItems: () => {},
  selectItemsSearchApi: () => {},
};

const CancelOrderPopup = ({
  themeContext,
  stringsData,
  isCancelOrderPopupLoading,
  cancelOrderReason,
  handleCancelOrderReasonRadioButtonClick,
  cancelOrderReasons,
  handleCancelOrderPopupConfirmBtnClick,
  handleCancelOrderPopupDismissBtnClick,
  cancelOrderReasonIsRequiredError,
  cancelOrderNote,
  handleCancelOrderNoteChange,
}) => (
  <Popup showCloseBtn={false}>
    <OrderDetailsScheduleOrderPopupWrapper>
      <OrderDetailsScheduleOrderPopupTitleWrapper>
        <OrderDetailsScheduleOrderPopupTitle as="p">
          {stringsData.orderDetailsHeader.cancelOrderTitle}
        </OrderDetailsScheduleOrderPopupTitle>
        <OrderDetailsScheduleOrderPopupTitleSpan
          as="span"
          onClick={() => handleCancelOrderPopupDismissBtnClick()}
        />
      </OrderDetailsScheduleOrderPopupTitleWrapper>
      <SectionDivider
        gap={16}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <OrderDetailsScheduleOrderReasonsWrapper>
        {cancelOrderReasons?.data.map(reasonCategory => {
          let scheduleOrderReasonsArr = [];
          scheduleOrderReasonsArr = reasonCategory.orderStateChildReasons.map(reason => ({
            name: reason.name,
            key: reason.id,
          }));
          return (
            <>
              <OrderDetailsCancelSectionSubTitle as="p">
                {reasonCategory.name}
              </OrderDetailsCancelSectionSubTitle>
              <RadioButtonGroup
                RadioButtonGroupArr={scheduleOrderReasonsArr}
                selectedRadioButton={cancelOrderReason}
                handleRadioButtonClick={handleCancelOrderReasonRadioButtonClick}
              />
              <SectionDivider
                gap={16}
                withSeparator
                separatorGap={16}
                borderColor={themeContext.colors.grey004}
              />
            </>
          );
        })}
        {cancelOrderReasonIsRequiredError && (
          <OrderDetailsScheduleOrderError>
            {stringsData.orderDetailsHeader.cancelOrderReasonIsRequired}
          </OrderDetailsScheduleOrderError>
        )}
      </OrderDetailsScheduleOrderReasonsWrapper>
      <OrderDetailsScheduleOrderNoteWrapper>
        <OrderDetailsScheduleOrderSectionTitle as="p">
          {stringsData.orderDetailsHeader.scheduleOrderNoteTitle}
        </OrderDetailsScheduleOrderSectionTitle>
        <SectionDivider gap={8} />
        <OrderDetailsScheduleOrderNoteTextArea
          placeholder={stringsData.orderDetailsHeader.scheduleOrderNotePlaceholder}
          value={cancelOrderNote}
          onChange={event => handleCancelOrderNoteChange(event)}
        />
      </OrderDetailsScheduleOrderNoteWrapper>
      <SectionDivider
        gap={22}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <OrderDetailsScheduleOrderBtnWrapper>
        <OrderDetailsScheduleOrderCancelBtn
          as="button"
          onClick={() => handleCancelOrderPopupDismissBtnClick()}
        >
          {stringsData.orderDetailsHeader.scheduleOrderCancelBtn}
        </OrderDetailsScheduleOrderCancelBtn>
        <OrderDetailsScheduleOrderSaveBtn
          as="button"
          onClick={() => handleCancelOrderPopupConfirmBtnClick()}
        >
          {(isCancelOrderPopupLoading && <Loader width={14} height={14} thickness={4} />) ||
            stringsData.orderDetailsHeader.scheduleOrderSaveBtn}
        </OrderDetailsScheduleOrderSaveBtn>
      </OrderDetailsScheduleOrderBtnWrapper>
    </OrderDetailsScheduleOrderPopupWrapper>
  </Popup>
);
CancelOrderPopup.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  stringsData: PropTypes.shape({
    orderDetailsHeader: PropTypes.shape({
      cancelOrderTitle: PropTypes.string,
      scheduleOrderSaveBtn: PropTypes.string,
      scheduleOrderCancelBtn: PropTypes.string,
      cancelOrderReasonIsRequired: PropTypes.string,
      scheduleOrderNoteTitle: PropTypes.string,
      scheduleOrderNotePlaceholder: PropTypes.string,
    }),
  }).isRequired,
  isCancelOrderPopupLoading: PropTypes.bool.isRequired,
  cancelOrderReason: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  handleCancelOrderReasonRadioButtonClick: PropTypes.func.isRequired,
  handleCancelOrderPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleCancelOrderPopupDismissBtnClick: PropTypes.func.isRequired,
  cancelOrderReasons: PropTypes.shape({
    status: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  cancelOrderReasonIsRequiredError: PropTypes.bool,
  cancelOrderNote: PropTypes.string.isRequired,
  handleCancelOrderNoteChange: PropTypes.func.isRequired,
};
CancelOrderPopup.defaultProps = {
  cancelOrderReasonIsRequiredError: false,
};

const RejectOrderPopup = ({
  themeContext,
  stringsData,
  isRejectOrderPopupLoading,
  rejectOrderReason,
  handleRejectOrderReasonRadioButtonClick,
  rejectOrderReasons,
  handleRejectOrderPopupConfirmBtnClick,
  handleRejectOrderPopupDismissBtnClick,
  rejectOrderReasonIsRequiredError,
  rejectOrderNote,
  handleRejectOrderNoteChange,
  orderData,
  selectedItems,
  setSelectedItems,
  selectItemsSearchApi,
}) => (
  <Popup showCloseBtn={false}>
    <OrderDetailsScheduleOrderPopupWrapper>
      <OrderDetailsScheduleOrderPopupTitleWrapper>
        <OrderDetailsScheduleOrderPopupTitle as="p">
          {stringsData.orderDetailsHeader.rejectOrderTitle}
        </OrderDetailsScheduleOrderPopupTitle>
        <OrderDetailsScheduleOrderPopupTitleSpan
          as="span"
          onClick={() => handleRejectOrderPopupDismissBtnClick()}
        />
      </OrderDetailsScheduleOrderPopupTitleWrapper>
      <SectionDivider
        gap={16}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <OrderDetailsScheduleOrderReasonsWrapper>
        {rejectOrderReasons?.data.map(reasonCategory => {
          let scheduleOrderReasonsArr = [];
          scheduleOrderReasonsArr = reasonCategory.orderStateChildReasons.map(reason => ({
            name: reason.name,
            key: reason.id,
          }));
          return (
            <>
              <OrderDetailsCancelSectionSubTitle as="p">
                {reasonCategory.name}
              </OrderDetailsCancelSectionSubTitle>
              <RadioButtonGroup
                RadioButtonGroupArr={scheduleOrderReasonsArr}
                selectedRadioButton={rejectOrderReason}
                handleRadioButtonClick={handleRejectOrderReasonRadioButtonClick}
              />
              {reasonCategory.id === 1 &&
                renderSelectItemsSection(
                  orderData,
                  rejectOrderReason,
                  selectedItems,
                  setSelectedItems,
                  selectItemsSearchApi,
                )}
              <SectionDivider
                gap={16}
                withSeparator
                separatorGap={16}
                borderColor={themeContext.colors.grey004}
              />
            </>
          );
        })}
        {rejectOrderReasonIsRequiredError && (
          <OrderDetailsScheduleOrderError>
            {stringsData.orderDetailsHeader.cancelOrderReasonIsRequired}
          </OrderDetailsScheduleOrderError>
        )}
      </OrderDetailsScheduleOrderReasonsWrapper>
      <OrderDetailsScheduleOrderNoteWrapper>
        <OrderDetailsScheduleOrderSectionTitle as="p">
          {stringsData.orderDetailsHeader.scheduleOrderNoteTitle}
        </OrderDetailsScheduleOrderSectionTitle>
        <SectionDivider gap={8} />
        <OrderDetailsScheduleOrderNoteTextArea
          placeholder={stringsData.orderDetailsHeader.scheduleOrderNotePlaceholder}
          value={rejectOrderNote}
          onChange={event => handleRejectOrderNoteChange(event)}
        />
      </OrderDetailsScheduleOrderNoteWrapper>
      <SectionDivider
        gap={22}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <OrderDetailsScheduleOrderBtnWrapper>
        <OrderDetailsScheduleOrderCancelBtn
          as="button"
          onClick={() => handleRejectOrderPopupDismissBtnClick()}
        >
          {stringsData.orderDetailsHeader.scheduleOrderCancelBtn}
        </OrderDetailsScheduleOrderCancelBtn>
        <OrderDetailsScheduleOrderSaveBtn
          as="button"
          onClick={() => handleRejectOrderPopupConfirmBtnClick()}
          disabled={
            reasonsToEnableSelectItemsSection[rejectOrderReason] && selectedItems.length === 0
          }
        >
          {(isRejectOrderPopupLoading && <Loader width={14} height={14} thickness={4} />) ||
            stringsData.orderDetailsHeader.scheduleOrderSaveBtn}
        </OrderDetailsScheduleOrderSaveBtn>
      </OrderDetailsScheduleOrderBtnWrapper>
    </OrderDetailsScheduleOrderPopupWrapper>
  </Popup>
);
RejectOrderPopup.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  stringsData: PropTypes.shape({
    orderDetailsHeader: PropTypes.shape({
      rejectOrderTitle: PropTypes.string,
      scheduleOrderSaveBtn: PropTypes.string,
      scheduleOrderCancelBtn: PropTypes.string,
      cancelOrderReasonIsRequired: PropTypes.string,
      rejectOrderReasonTitle: PropTypes.string,
      scheduleOrderNoteTitle: PropTypes.string,
      scheduleOrderNotePlaceholder: PropTypes.string,
    }),
  }).isRequired,
  isRejectOrderPopupLoading: PropTypes.bool.isRequired,
  rejectOrderReason: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  handleRejectOrderReasonRadioButtonClick: PropTypes.func.isRequired,
  handleRejectOrderPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleRejectOrderPopupDismissBtnClick: PropTypes.func.isRequired,
  rejectOrderReasons: PropTypes.shape({
    status: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  rejectOrderReasonIsRequiredError: PropTypes.bool,
  rejectOrderNote: PropTypes.string.isRequired,
  handleRejectOrderNoteChange: PropTypes.func.isRequired,
  orderData: PropTypes.shape({}),
  selectedItems: PropTypes.shape([]),
  setSelectedItems: PropTypes.func,
  selectItemsSearchApi: PropTypes.func,
};
RejectOrderPopup.defaultProps = {
  rejectOrderReasonIsRequiredError: false,
  orderData: {},
  selectedItems: [],
  setSelectedItems: () => {},
  selectItemsSearchApi: () => {},
};
// ###############################################################
// <-------------------- End Sub Sections ----------------------->
// ###############################################################

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const OrderDetailsHeader = ({
  stringsData,
  orderDetails,
  actionPopupStatus,
  handleOrderRefreshBtnClick,
  actionBtnClick,
  showBackActionBtn,
  actionPopupDismissBtn,
  actionPopupConfirmBtn,
  actionPopupConfirmBtnCallback,
  handleTextToCopyToClipboardClick,
  scheduleOrderPopupConfirmBtn,
  cancelOrderPopupConfirmBtn,
  rejectOrderPopupConfirmBtn,
  fetchScheduleOrderReasons,
  fetchCancelOrderReasons,
  fetchRejectOrderReasons,
  cancelOrderReasons,
  rejectOrderReasons,
  scheduleOrderReasons,
  loginInfo,
  orderStatusEnum,
  hideButtonBasedOnState,
  showRejectButton,
  selectItemsSearchApi,
  buttonDisabledOptions,
  dynamicPermissions,
  initialButtonOptions,
  initialNextOrderState,
  permissionsCategories,
}) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const generateTimeSlotsArr = selectedDate => {
    const timeSlotsArr = [];
    const checkIfToday = checkIfDateIsToday(selectedDate);
    const date = checkIfToday ? new Date() : selectedDate;
    const hours = checkIfToday ? date.getHours() + 3 : date.getHours();
    const minutes = date.getMinutes();
    const dayPart = ['am', 'pm'];

    for (let i = hours; i < 24; i += 1) {
      for (let j = i === hours ? Math.ceil(minutes / 15) : 0; j < 4; j += 1) {
        // eslint-disable-next-line no-bitwise
        timeSlotsArr.push(`${i % 12 || 12}:${j * 15 || '00'} ${dayPart[(i / 12) | 0]}`);
      }
    }

    return timeSlotsArr.map(slot => ({
      key: slot,
      name: slot,
    }));
  };
  const dispatch = useDispatch();
  const extraActionMenuwrapperRef = useRef(null);
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [isExtraActionsMenuOpen, setIsExtraActionsMenuOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isActionPopupOpen, setIsActionPopupOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isActionPopupLoading, setIsActionPopupLoading] = useState(false);
  const [isScheduleOrderPopupOpen, setIsScheduleOrderPopupOpen] = useState(false);
  const [isScheduleOrderPopupLoading, setIsScheduleOrderPopupLoading] = useState(false);
  const [scheduleOrderSelectedDate, setScheduleOrderSelectedDate] = useState(new Date());
  const [scheduleOrderReason, setScheduleOrderReason] = useState('');
  const [scheduleOrderNote, setScheduleOrderNote] = useState('');
  const [buttonOptions, setButtonOptions] = useState(initialButtonOptions);
  const [nextOrderState, setNextOrderState] = useState(initialNextOrderState);

  const scheduleOrderTimeSlotsArr = useMemo(
    () => generateTimeSlotsArr(scheduleOrderSelectedDate),
    [scheduleOrderSelectedDate],
  );
  const [scheduleOrderSelectedTime, setScheduleOrderSelectedTime] = useState(
    scheduleOrderTimeSlotsArr[0],
  );
  const [scheduleOrderReasonIsRequiredError, setScheduleOrderReasonIsRequiredError] =
    useState(false);

  const [isCancelOrderPopupOpen, setIsCancelOrderPopupOpen] = useState(false);
  const [isCancelOrderPopupLoading, setIsCancelOrderPopupLoading] = useState(false);
  const [cancelOrderReason, setCancelOrderReason] = useState('');
  const [cancelOrderReasonIsRequiredError, setCancelOrderReasonIsRequiredError] = useState(false);
  const [cancelOrderNote, setCancelOrderNote] = useState('');

  const [isRejectOrderPopupOpen, setIsRejectOrderPopupOpen] = useState(false);
  const [isRejectOrderPopupLoading, setIsRejectOrderPopupLoading] = useState(false);
  const [rejectOrderReason, setRejectOrderReason] = useState('');
  const [rejectOrderReasonIsRequiredError, setRejectOrderReasonIsRequiredError] = useState(false);
  const [rejectOrderNote, setRejectOrderNote] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  useEffect(() => {
    setScheduleOrderSelectedTime(scheduleOrderTimeSlotsArr[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleOrderSelectedDate]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        extraActionMenuwrapperRef.current &&
        extraActionMenuwrapperRef.current.contains(event.target) === false
      )
        setIsExtraActionsMenuOpen(false);
    };
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [extraActionMenuwrapperRef]);

  useEffect(() => {
    dispatch(fetchScheduleOrderReasons());
    dispatch(fetchCancelOrderReasons());
    if (showRejectButton) dispatch(fetchRejectOrderReasons());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  const themeContext = useContext(ThemeContext);
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  const actionPopupConfirmBtnCallbackFn = shouldReloadOrderDetails => {
    setIsActionPopupLoading(false);
    setIsActionPopupOpen(false);

    actionPopupConfirmBtnCallback(shouldReloadOrderDetails);
  };

  const scheduleOrderPopupConfirmBtnCallbackFn = shouldReloadOrderDetails => {
    setIsScheduleOrderPopupLoading(false);
    setIsScheduleOrderPopupOpen(false);

    actionPopupConfirmBtnCallback(shouldReloadOrderDetails);
  };

  const cancelOrderPopupConfirmBtnCallbackFn = shouldReloadOrderDetails => {
    setIsCancelOrderPopupLoading(false);
    setIsCancelOrderPopupOpen(false);

    actionPopupConfirmBtnCallback(shouldReloadOrderDetails);
  };

  const rejectOrderPopupConfirmBtnCallbackFn = shouldReloadOrderDetails => {
    setIsRejectOrderPopupLoading(false);
    setIsRejectOrderPopupOpen(false);

    actionPopupConfirmBtnCallback(shouldReloadOrderDetails);
  };
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleActionBtnClick = actionStatus => {
    setIsActionPopupOpen(true);

    actionBtnClick(actionStatus);
  };

  // eslint-disable-next-line no-unused-vars
  const handleActionPopupDismissBtnClick = () => {
    setIsActionPopupOpen(false);

    actionPopupDismissBtn();
  };

  // eslint-disable-next-line no-unused-vars
  const handleActionPopupConfirmBtnClick = () => {
    setIsActionPopupLoading(true);

    actionPopupConfirmBtn(actionPopupConfirmBtnCallbackFn);
  };

  const handleExtraActionsBtnClick = () => {
    setIsExtraActionsMenuOpen(prevState => !prevState);
  };

  const handleCancelActionBtnClick = () => {
    setIsExtraActionsMenuOpen(false);
    setIsCancelOrderPopupOpen(true);
    // handleActionBtnClick(orderStatusEnum.Cancelled);
  };

  const handleRejectActionBtnClick = () => {
    setIsExtraActionsMenuOpen(false);
    setIsRejectOrderPopupOpen(true);
  };

  const handleScheduleOrderBtnClick = () => {
    setIsScheduleOrderPopupOpen(true);
  };

  const handleScheduleOrderDatePickerChange = date => {
    setScheduleOrderSelectedDate(date);
  };

  const handleScheduleOrderTimeSlotDropdownMenuChange = timeSlot => {
    setScheduleOrderSelectedTime(timeSlot);
  };

  const handleScheduleOrderPopupDismissBtnClick = () => {
    setIsScheduleOrderPopupOpen(false);
    setSelectedItems([]);
  };

  const handleScheduleOrderReasonRadioButtonClick = reason => {
    setScheduleOrderReasonIsRequiredError(false);
    setScheduleOrderReason(reason);
    setSelectedItems([]);
  };

  const handleScheduleOrderNoteChange = event => {
    const { value } = event.target;

    setScheduleOrderNote(value);
  };

  const handleScheduleOrderPopupConfirmBtnClick = () => {
    if (!scheduleOrderReason) {
      setScheduleOrderReasonIsRequiredError(true);
      return;
    }
    setIsScheduleOrderPopupLoading(true);

    const { hours, minutes } = getHoursAndMinutes(scheduleOrderSelectedTime.key);
    const choosenDate = new Date(scheduleOrderSelectedDate);
    choosenDate.setHours(0, 0, 0, 0);
    choosenDate.setHours(choosenDate.getHours() + hours);
    choosenDate.setMinutes(choosenDate.getMinutes() + minutes);
    const dateGMT = new Date(choosenDate);
    const ScheduledToDataTime = dateGMT.toISOString();

    const orderScheduleStatus = {
      entityTypeId: 3,
      orderStateTypeId: 38,
    };

    const data = {
      entityId: loginInfo.details.data.key,
      entityTypeId: orderScheduleStatus.entityTypeId,
      orderKey: orderDetails.details.key,
      orderStateTypeId: orderScheduleStatus.orderStateTypeId,
      orderStateReason: {
        orderStateChildReasonId: scheduleOrderReason,
        ...(selectedItems.length > 0 && {
          productKeys: selectedItems.map(({ serviceKey }) => serviceKey),
        }),
      },
      scheduledTo: ScheduledToDataTime,
    };
    scheduleOrderPopupConfirmBtn(data, scheduleOrderPopupConfirmBtnCallbackFn);
  };

  const handleCancelOrderReasonRadioButtonClick = reason => {
    setCancelOrderReasonIsRequiredError(false);
    setCancelOrderReason(reason);
  };

  const handleCancelOrderPopupDismissBtnClick = () => {
    setIsCancelOrderPopupOpen(false);
  };

  const handleCancelOrderPopupConfirmBtnClick = () => {
    if (!cancelOrderReason) {
      setCancelOrderReasonIsRequiredError(true);
      return;
    }
    setIsCancelOrderPopupLoading(true);

    const orderCancelStatus = {
      entityTypeId: 3,
      orderStateTypeId: 37,
    };

    const data = {
      entityId: loginInfo.details.data.key,
      entityTypeId: orderCancelStatus.entityTypeId,
      orderKey: orderDetails.details.key,
      orderStateTypeId: orderCancelStatus.orderStateTypeId,
      note: cancelOrderNote,
      orderStateReason: {
        orderStateChildReasonId: cancelOrderReason,
      },
    };
    cancelOrderPopupConfirmBtn(data, cancelOrderPopupConfirmBtnCallbackFn);
  };

  const handleCancelOrderNoteChange = event => {
    const { value } = event.target;
    setCancelOrderNote(value);
  };

  const handleRejectOrderReasonRadioButtonClick = reason => {
    setRejectOrderReasonIsRequiredError(false);
    setRejectOrderReason(reason);
    setSelectedItems([]);
  };

  const handleRejectOrderPopupDismissBtnClick = () => {
    setIsRejectOrderPopupOpen(false);
    setSelectedItems([]);
  };

  const handleRejectOrderPopupConfirmBtnClick = () => {
    if (!rejectOrderReason) {
      setRejectOrderReasonIsRequiredError(true);
      return;
    }
    setIsRejectOrderPopupLoading(true);
    const data = {
      entityId: loginInfo.details.data.key,
      entityTypeId: 3,
      orderKey: orderDetails.details.key,
      orderStateTypeId: 15,
      note: rejectOrderNote,
      orderStateReason: {
        orderStateChildReasonId: rejectOrderReason,
        ...(selectedItems.length > 0 && {
          productKeys: selectedItems.map(({ serviceKey }) => serviceKey),
        }),
      },
    };
    rejectOrderPopupConfirmBtn(data, rejectOrderPopupConfirmBtnCallbackFn);
  };

  const handleRejectOrderNoteChange = event => {
    const { value } = event.target;
    setRejectOrderNote(value);
  };

  const disableNextOrderStateButton = () => {
    const orderDetailsTemp = orderDetails.details;
    if (
      orderDetailsTemp &&
      (orderDetailsTemp.orderReceipt === null || orderDetailsTemp.orderReceipt === {}) &&
      orderDetailsTemp.orderStateTypeId === 7 &&
      orderDetailsTemp.optionalPaymentMethodKey === paymentMethodsKeys.vezeetaCash
    )
      return true;
    if (buttonDisabledOptions[orderDetailsTemp.orderStateTypeId]) return true;
    return false;
  };

  useEffect(() => {
    const orderDetailsTemp = orderDetails.details;
    if (
      orderDetailsTemp &&
      orderDetailsTemp.optionalPaymentMethodKey !== null &&
      orderDetailsTemp.optionalPaymentMethodKey === paymentMethodsKeys.vezeetaCash
    ) {
      setButtonOptions(prevState => ({ ...prevState, 7: 'Payment Pending' }));
      setNextOrderState(prevState => ({ ...prevState, 7: '19' }));
    }
  }, [orderDetails]);

  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  const orderData = orderDetails.details;
  const {
    orderNumber,
    orderStateTypeId: orderStatus,
    orderItems,
    attachedResults,
    orderReceipt,
  } = orderData;

  initialNextOrderStateDefault, initialNextOrderStateLabelDefault

  const nextStateBtnLabel = initialNextOrderStateLabelDefault[orderStatus];
  const nextStateId = initialNextOrderStateDefault[orderStatus];
  const disableNextStateBtn = false;

  return (
    <>
      <OrderDetailsHeaderWrapper>
        <OrderDetailsHeaderOrderInfoWrapper>
          {showBackActionBtn && <OrderDetailsHeaderIconBtn icon={"'\\e906'"} />}
          <OrderDetailsHeaderOrderId onClick={() => handleTextToCopyToClipboardClick(orderNumber)}>
            {`${stringsData.orderDetailsHeader.orderId}${orderData.orderNumber}`}
          </OrderDetailsHeaderOrderId>
        </OrderDetailsHeaderOrderInfoWrapper>
        <OrderDetailsHeaderOrderBtnWrapper>
          {/** Next State Btn */}
          {nextStateBtnLabel && (
              <OrderDetailsHeaderViewedBtn
                  as="button"
                  disabled={
                    !(
                      (orderStatus === orderStatusEnum['Preparation Call And Itemization'] && orderItems.length > 0) ||
                      (orderStatus === orderStatusEnum['Sample Dropped'] && typeof attachedResults !== 'string') ||
                      (orderStatus !== orderStatusEnum['Sample Dropped'] || orderStatus !== orderStatusEnum['Preparation Call And Itemization'])
                      )}
                  onClick={() => {
                    handleActionBtnClick(nextStateId);
                  }}
                >
                  {nextStateBtnLabel}
              </OrderDetailsHeaderViewedBtn>
          )}

          {/** Rescheduke Btn */}
          {hideButtonBasedOnState?.schedule && !hideButtonBasedOnState?.schedule[orderStatus] && (
            <Can
              permissionsCategory={permissionsCategories?.order}
              perform="order:markAsScheduled"
              renderYes={() => (
                <OrderDetailsHeaderScheduleBtn
                  as="button"
                  onClick={() => handleScheduleOrderBtnClick()}
                >
                  {stringsData.orderDetailsHeader.scheduleBtnText}
                </OrderDetailsHeaderScheduleBtn>
              )}
              renderNo={() => null}
            />
          )}
          {/** Reject & Cancel */}
          <OrderDetailsHeaderIconBtnWrapper ref={extraActionMenuwrapperRef}>
            {Object.keys(hideButtonBasedOnState).length > 0 &&
              !hideButtonBasedOnState?.cancel[orderStatus] && (
                <Can
                  permissionsCategory={permissionsCategories?.order}
                  fallBackPermissions={['order:markAsCanceled', 'order:markAsRejectedOrder']}
                  renderYes={() => (
                    <OrderDetailsHeaderIconBtn
                      icon={"'\\e909'"}
                      onClick={() => handleExtraActionsBtnClick()}
                    />
                  )}
                  renderNo={() => null}
                />
              )}
            {isExtraActionsMenuOpen && (
              <OrderDetailsHeaderIconBtnMenu>
                <Can
                  permissionsCategory={permissionsCategories?.order}
                  perform="order:markAsCanceled"
                  renderYes={() => (
                    <OrderDetailsHeaderIconBtnMenuItem onClick={() => handleCancelActionBtnClick()}>
                      {stringsData.orderDetailsHeader.cancelBtnText}
                    </OrderDetailsHeaderIconBtnMenuItem>
                  )}
                  renderNo={() => null}
                />
                {showRejectButton && (
                  <Can
                    permissionsCategory={permissionsCategories?.order}
                    perform="order:markAsRejected"
                    renderYes={() => (
                      <OrderDetailsHeaderIconBtnMenuItem
                        onClick={() => handleRejectActionBtnClick()}
                      >
                        {stringsData.orderDetailsHeader.rejectBtnText}
                      </OrderDetailsHeaderIconBtnMenuItem>
                    )}
                    renderNo={() => null}
                  />
                )}
              </OrderDetailsHeaderIconBtnMenu>
            )}
          </OrderDetailsHeaderIconBtnWrapper>
          {/** Refresh Btn */}
          <OrderDetailsHeaderIconBtn
            icon={"'\\e90b'"}
            onClick={() => handleOrderRefreshBtnClick()}
          />
        </OrderDetailsHeaderOrderBtnWrapper>
      </OrderDetailsHeaderWrapper>
      {isCancelOrderPopupOpen && (
        <CancelOrderPopup
          themeContext={themeContext}
          stringsData={stringsData}
          isCancelOrderPopupLoading={isCancelOrderPopupLoading}
          cancelOrderReason={cancelOrderReason}
          handleCancelOrderReasonRadioButtonClick={handleCancelOrderReasonRadioButtonClick}
          cancelOrderReasons={cancelOrderReasons}
          cancelOrderReasonIsRequiredError={cancelOrderReasonIsRequiredError}
          handleCancelOrderPopupConfirmBtnClick={handleCancelOrderPopupConfirmBtnClick}
          handleCancelOrderPopupDismissBtnClick={handleCancelOrderPopupDismissBtnClick}
          handleCancelOrderNoteChange={handleCancelOrderNoteChange}
          cancelOrderNote={cancelOrderNote}
        />
      )}
      {isRejectOrderPopupOpen && (
        <RejectOrderPopup
          themeContext={themeContext}
          stringsData={stringsData}
          isRejectOrderPopupLoading={isRejectOrderPopupLoading}
          rejectOrderReason={rejectOrderReason}
          handleRejectOrderReasonRadioButtonClick={handleRejectOrderReasonRadioButtonClick}
          rejectOrderReasons={rejectOrderReasons}
          rejectOrderReasonIsRequiredError={rejectOrderReasonIsRequiredError}
          handleRejectOrderPopupConfirmBtnClick={handleRejectOrderPopupConfirmBtnClick}
          handleRejectOrderPopupDismissBtnClick={handleRejectOrderPopupDismissBtnClick}
          handleRejectOrderNoteChange={handleRejectOrderNoteChange}
          rejectOrderNote={rejectOrderNote}
          orderData={orderData}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          selectItemsSearchApi={selectItemsSearchApi}
        />
      )}
      {isActionPopupOpen && (
        <OrderActionsPopup
          themeContext={themeContext}
          stringsData={stringsData}
          isActionPopupLoading={isActionPopupLoading}
          actionStatus={actionPopupStatus}
          handleActionPopupConfirmBtnClick={handleActionPopupConfirmBtnClick}
          handleActionPopupDismissBtnClick={handleActionPopupDismissBtnClick}
          orderStatusEnum={orderStatusEnum}
        />
      )}
      {isScheduleOrderPopupOpen && (
        <ScheduleOrderPopup
          themeContext={themeContext}
          stringsData={stringsData}
          isScheduleOrderPopupLoading={isScheduleOrderPopupLoading}
          scheduleOrderSelectedDate={scheduleOrderSelectedDate}
          scheduleOrderTimeSlotsArr={scheduleOrderTimeSlotsArr}
          scheduleOrderSelectedTime={scheduleOrderSelectedTime}
          scheduleOrderReason={scheduleOrderReason}
          scheduleOrderNote={scheduleOrderNote}
          handleScheduleOrderDatePickerChange={handleScheduleOrderDatePickerChange}
          handleScheduleOrderTimeSlotDropdownMenuChange={
            handleScheduleOrderTimeSlotDropdownMenuChange
          }
          handleScheduleOrderReasonRadioButtonClick={handleScheduleOrderReasonRadioButtonClick}
          handleScheduleOrderNoteChange={handleScheduleOrderNoteChange}
          handleScheduleOrderPopupConfirmBtnClick={handleScheduleOrderPopupConfirmBtnClick}
          handleScheduleOrderPopupDismissBtnClick={handleScheduleOrderPopupDismissBtnClick}
          scheduleOrderReasons={scheduleOrderReasons}
          scheduleOrderReasonIsRequiredError={scheduleOrderReasonIsRequiredError}
          orderData={orderData}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          selectItemsSearchApi={selectItemsSearchApi}
        />
      )}
    </>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

OrderDetailsHeader.propTypes = {
  stringsData: PropTypes.shape({
    orderDetailsHeader: PropTypes.shape({
      orderId: PropTypes.string,
      viewBtnText: PropTypes.string,
      processedBtnText: PropTypes.string,
      resultsDoneBtnText: PropTypes.string,
      scheduleBtnText: PropTypes.string,
      cancelBtnText: PropTypes.string,
      rejectBtnText: PropTypes.string,
    }),
  }).isRequired,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      key: PropTypes.string,
      orderNumber: PropTypes.string,
      orderStateTypeId: PropTypes.number,
      orderItems: PropTypes.arrayOf(PropTypes.shape({})),
      attachedResults: PropTypes.oneOfType([
        PropTypes.shape({
          attachments: PropTypes.arrayOf(PropTypes.shape({})),
        }),
        PropTypes.string,
      ]),
      orderReceipt: PropTypes.shape({}),
      optionalPaymentMethodKey: PropTypes.string,
    }),
  }).isRequired,
  loginInfo: PropTypes.shape({
    details: PropTypes.shape({
      data: PropTypes.shape({
        key: PropTypes.string,
      }),
    }),
  }).isRequired,
  actionPopupStatus: PropTypes.number.isRequired,
  showBackActionBtn: PropTypes.bool,
  showRejectButton: PropTypes.bool,
  handleOrderRefreshBtnClick: PropTypes.func.isRequired,
  actionBtnClick: PropTypes.func.isRequired,
  actionPopupDismissBtn: PropTypes.func.isRequired,
  actionPopupConfirmBtn: PropTypes.func.isRequired,
  actionPopupConfirmBtnCallback: PropTypes.func.isRequired,
  handleTextToCopyToClipboardClick: PropTypes.func.isRequired,
  scheduleOrderPopupConfirmBtn: PropTypes.func.isRequired,
  cancelOrderPopupConfirmBtn: PropTypes.func.isRequired,
  rejectOrderPopupConfirmBtn: PropTypes.func,
  scheduleOrderReasons: PropTypes.shape({
    status: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  fetchScheduleOrderReasons: PropTypes.func.isRequired,
  fetchCancelOrderReasons: PropTypes.func.isRequired,
  fetchRejectOrderReasons: PropTypes.func,
  cancelOrderReasons: PropTypes.shape({
    status: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  rejectOrderReasons: PropTypes.shape({
    status: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  orderStatusEnum: PropTypes.shape({
    'Ready To Be Processed': PropTypes.number,
    Received: PropTypes.number,
    'Preparation Call And Itemization': PropTypes.number,
    'Sample Dropped': PropTypes.number,
    'Results Sent': PropTypes.number,
    'Preparation Completed': PropTypes.number,
    Cancelled: PropTypes.number,
  }).isRequired,
  hideButtonBasedOnState: PropTypes.shape({
    next: PropTypes.shape({}),
    schedule: PropTypes.shape({}),
    cancel: PropTypes.shape({}),
  }).isRequired,
  selectItemsSearchApi: PropTypes.func,
  buttonDisabledOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dynamicPermissions: PropTypes.shape({}),
  initialButtonOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  initialNextOrderState: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  permissionsCategories: PropTypes.shape({
    order: PropTypes.string,
  }),
};

OrderDetailsHeader.defaultProps = {
  showBackActionBtn: false,
  showRejectButton: false,
  rejectOrderReasons: {},
  rejectOrderPopupConfirmBtn: () => {},
  fetchRejectOrderReasons: () => {},
  selectItemsSearchApi: () => {},
  dynamicPermissions: {},
  permissionsCategories: {},
};

export default OrderDetailsHeader;
